/**
 * Price Notifications — list view · Version 1.1 · Sprint 1 · M1.
 *
 * Operators land here every morning to drop the Suncor Price Change
 * Notification PDF. The page lists prior notifications and provides
 * the drag-and-drop upload that flows into review + approve.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { UploadSimple, FileText, CheckCircle, Clock, ArrowRight } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { fmtCalendarDateShort } from "@/lib/calendarDate";

const INTERNAL_PRODUCT_LABEL = {
  regular: "Regular",
  premium: "Premium",
  clear_diesel: "Clear Diesel",
  dyed_diesel: "Dyed Diesel",
  def: "DEF",
};

export default function PriceNotifications() {
  const nav = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/price-notifications");
      setRows(data.notifications || []);
    } catch (e) {
      toast.error("Could not load daily pricing");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith(".pdf")) {
      toast.error("Please upload a PDF file.");
      return;
    }
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/price-notifications/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Notification parsed — review before approving.");
      nav(`/price-notifications/${data.id}`);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : detail?.message || "Upload failed";
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragActive(false);
    const file = e.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <div className="space-y-6" data-testid="price-notifications-page">
      <header className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display text-2xl font-semibold text-ink-primary">Daily Pricing</h1>
          <p className="text-sm text-ink-secondary">
            Drop a Suncor supplier rack price PDF to parse, review, approve, and distribute today's Daily Buy Prices to drivers.
            Approval posts today's supplier rack prices; drivers are notified only when you click Send Driver Notice.
          </p>
        </div>
      </header>

      {/* Drop zone */}
      <div
        data-testid="price-notification-dropzone"
        onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
        onDragLeave={() => setDragActive(false)}
        onDrop={onDrop}
        onClick={() => fileInputRef.current?.click()}
        className={[
          "flex cursor-pointer flex-col items-center justify-center gap-3 rounded-lg border-2 border-dashed px-6 py-12 text-center transition-colors",
          dragActive
            ? "border-brand bg-brand/5"
            : "border-divider bg-surface-alt hover:border-brand/60 hover:bg-brand/5",
        ].join(" ")}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          data-testid="price-notification-file-input"
          onChange={(e) => doUpload(e.target.files?.[0])}
        />
        <UploadSimple size={40} weight="regular" className={dragActive ? "text-brand" : "text-ink-muted"} />
        <div className="text-base font-semibold text-ink-primary">
          {uploading ? "Parsing notification…" : "Drop Suncor PDF here or click to browse"}
        </div>
        <div className="text-xs text-ink-secondary">
          Sprint 1 supports Suncor Price Change Notifications only.
        </div>
      </div>

      {/* History */}
      <section>
        <h2 className="mb-3 font-display text-base font-semibold text-ink-primary">Recent notifications</h2>
        {loading ? (
          <div className="text-sm text-ink-secondary" data-testid="pn-loading">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="rounded-md border border-divider bg-surface p-6 text-sm text-ink-secondary" data-testid="pn-empty">
            No notifications yet. Drop your first Suncor PDF above.
          </div>
        ) : (
          <ul className="divide-y divide-divider rounded-md border border-divider bg-surface" data-testid="pn-list">
            {rows.map((r) => (
              <li key={r.id}>
                <button
                  onClick={() => nav(`/price-notifications/${r.id}`)}
                  data-testid={`pn-row-${r.id}`}
                  className="flex w-full items-center justify-between gap-4 px-4 py-3 text-left hover:bg-surface-alt"
                >
                  <div className="flex items-center gap-3">
                    <FileText size={20} className="text-ink-secondary" />
                    <div>
                      <div className="text-sm font-semibold text-ink-primary">
                        {r.supplier || "Unknown supplier"} · Effective {fmtCalendarDateShort(r.effective_date)}
                      </div>
                      <div className="text-[11px] text-ink-secondary">
                        Uploaded {new Date(r.uploaded_at).toLocaleString()} · by {r.uploaded_by}
                      </div>
                      {Array.isArray(r.mapped_products) && r.mapped_products.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1">
                          {r.mapped_products.map((p) => (
                            <span
                              key={p.internal_product_code}
                              className="rounded-full bg-surface-alt px-2 py-0.5 text-[10px] font-medium text-ink-secondary"
                            >
                              {INTERNAL_PRODUCT_LABEL[p.internal_product_code] || p.internal_product_code}
                              {" "}{p.new_price}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={r.status} />
                    <ArrowRight size={14} className="text-ink-muted" />
                  </div>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}

function StatusBadge({ status }) {
  if (status === "sent") {
    return (
      <span
        data-testid="pn-status-sent"
        className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-800"
      >
        <CheckCircle size={12} weight="fill" /> Sent
      </span>
    );
  }
  if (status === "approved") {
    return (
      <span
        data-testid="pn-status-approved"
        className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
      >
        <CheckCircle size={12} weight="fill" /> Approved
      </span>
    );
  }
  return (
    <span
      data-testid="pn-status-parsed"
      className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
    >
      <Clock size={12} weight="fill" /> Pending Review
    </span>
  );
}
